import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Card, CardHeader } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { employeeService } from '../services/employeeService';
import { Employee } from '../types';

type AssetType = 'laptop' | 'charger' | 'headphone' | 'phoneNumber';

interface Asset {
  type: AssetType;
  assetNumber: string;
  employeeId: number | null;
  employeeName: string;
  employeeCode: string;
  isUnassigned?: boolean; // Flag for assets stored in localStorage
}

const assetTypeLabels: Record<AssetType, string> = {
  laptop: 'Laptop',
  charger: 'Charger',
  headphone: 'Headphone',
  phoneNumber: 'Phone Number',
};

export function AssetManagementPage() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssetType, setFilterAssetType] = useState<AssetType | 'all'>('all');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  // Add Asset form state
  const [newAssetType, setNewAssetType] = useState<AssetType>('laptop');
  const [newAssetNumber, setNewAssetNumber] = useState('');
  const [newAssetEmployeeId, setNewAssetEmployeeId] = useState<string>('');
  // Bulk upload state
  const [bulkUploadFile, setBulkUploadFile] = useState<File | null>(null);
  const [bulkUploadProgress, setBulkUploadProgress] = useState<{
    total: number;
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Listen for storage changes to update unassigned assets display
  useEffect(() => {
    const handleStorageChange = () => {
      // Force component re-render by updating a state
      // This will cause getAllAssets to re-run and include updated unassigned assets
      fetchEmployees();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      // Fetch all employees
      const allEmployees: Employee[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await employeeService.getAll(page, 1000, { isActive: true });
        allEmployees.push(...response.data);

        if (page >= (response.pagination?.totalPages || 1)) {
          hasMore = false;
        } else {
          page++;
        }
      }

      setEmployees(allEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  // Get unassigned assets from localStorage
  const getUnassignedAssets = (): Asset[] => {
    try {
      const stored = localStorage.getItem('unassignedAssets');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error reading unassigned assets:', error);
    }
    return [];
  };

  // Save unassigned assets to localStorage
  const saveUnassignedAssets = (assets: Asset[]) => {
    try {
      localStorage.setItem('unassignedAssets', JSON.stringify(assets));
    } catch (error) {
      console.error('Error saving unassigned assets:', error);
    }
  };

  // Collect all assets from employees and unassigned assets
  const getAllAssets = (): Asset[] => {
    const assets: Asset[] = [];

    // Get assets assigned to employees
    employees.forEach((emp) => {
      if (emp.laptop) {
        assets.push({
          type: 'laptop',
          assetNumber: emp.laptop,
          employeeId: emp.id,
          employeeName: `${emp.firstName} ${emp.lastName}`,
          employeeCode: emp.employeeCode,
        });
      }
      if (emp.charger) {
        assets.push({
          type: 'charger',
          assetNumber: emp.charger,
          employeeId: emp.id,
          employeeName: `${emp.firstName} ${emp.lastName}`,
          employeeCode: emp.employeeCode,
        });
      }
      if (emp.headphone) {
        assets.push({
          type: 'headphone',
          assetNumber: emp.headphone,
          employeeId: emp.id,
          employeeName: `${emp.firstName} ${emp.lastName}`,
          employeeCode: emp.employeeCode,
        });
      }
      if (emp.phoneNumber) {
        assets.push({
          type: 'phoneNumber',
          assetNumber: emp.phoneNumber,
          employeeId: emp.id,
          employeeName: `${emp.firstName} ${emp.lastName}`,
          employeeCode: emp.employeeCode,
        });
      }
    });

    // Add unassigned assets from localStorage
    const unassignedAssets = getUnassignedAssets();
    assets.push(...unassignedAssets);

    return assets;
  };

  // Use useMemo to compute assets when employees change
  const assets = useMemo(() => getAllAssets(), [employees]);

  const filteredAssets = assets.filter((asset) => {
    const matchesSearch =
      asset.assetNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.employeeCode.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterAssetType === 'all' || asset.type === filterAssetType;
    return matchesSearch && matchesType;
  });

  const openAssignModal = (asset: Asset) => {
    setSelectedAsset(asset);
    setSelectedEmployeeId(asset.employeeId ? String(asset.employeeId) : '');
    setShowAssignModal(true);
  };

  const handleAssignAsset = async () => {
    if (!selectedAsset) return;

    setSubmitting(true);
    try {
      const updateData: any = {};

      // Clear the asset from the current employee if reassigning or unassigning
      if (selectedAsset.employeeId) {
        const currentEmployee = employees.find((e) => e.id === selectedAsset.employeeId);
        if (currentEmployee) {
          // Remove asset from current employee
          const currentEmployeeUpdate: any = {};
          if (selectedAsset.type === 'laptop') currentEmployeeUpdate.laptop = null;
          if (selectedAsset.type === 'charger') currentEmployeeUpdate.charger = null;
          if (selectedAsset.type === 'headphone') currentEmployeeUpdate.headphone = null;
          if (selectedAsset.type === 'phoneNumber') currentEmployeeUpdate.phoneNumber = null;

          await employeeService.update(selectedAsset.employeeId, currentEmployeeUpdate);
        }
      }

      // Assign to new employee
      if (selectedEmployeeId) {
        const newEmployeeId = parseInt(selectedEmployeeId);
        const newEmployee = employees.find((e) => e.id === newEmployeeId);
        if (selectedAsset.type === 'laptop') updateData.laptop = selectedAsset.assetNumber;
        if (selectedAsset.type === 'charger') updateData.charger = selectedAsset.assetNumber;
        if (selectedAsset.type === 'headphone') updateData.headphone = selectedAsset.assetNumber;
        if (selectedAsset.type === 'phoneNumber') updateData.phoneNumber = selectedAsset.assetNumber;

        await employeeService.update(newEmployeeId, updateData);
        toast.success(`Asset assigned successfully to ${newEmployee?.firstName} ${newEmployee?.lastName}`);
      } else {
        toast.success('Asset unassigned successfully');
      }

      setShowAssignModal(false);
      setSelectedAsset(null);
      setSelectedEmployeeId('');
      fetchEmployees();
    } catch (error) {
      console.error('Error assigning asset:', error);
      toast.error('Failed to assign asset');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAsset = async () => {
    if (!newAssetNumber.trim()) {
      toast.error('Please enter an asset number');
      return;
    }

    if (!newAssetEmployeeId) {
      toast.error('Please select an employee to assign the asset to');
      return;
    }

    setSubmitting(true);
    try {
      const employeeId = parseInt(newAssetEmployeeId);
      const updateData: any = {};

      // Check if employee already has this asset type
      const employee = employees.find((e) => e.id === employeeId);
      if (employee) {
        if (newAssetType === 'laptop' && employee.laptop) {
          toast.error('Employee already has a laptop assigned');
          setSubmitting(false);
          return;
        }
        if (newAssetType === 'charger' && employee.charger) {
          toast.error('Employee already has a charger assigned');
          setSubmitting(false);
          return;
        }
        if (newAssetType === 'headphone' && employee.headphone) {
          toast.error('Employee already has a headphone assigned');
          setSubmitting(false);
          return;
        }
        if (newAssetType === 'phoneNumber' && employee.phoneNumber) {
          toast.error('Employee already has a phone number assigned');
          setSubmitting(false);
          return;
        }

        // Assign the asset
        if (newAssetType === 'laptop') updateData.laptop = newAssetNumber.trim();
        if (newAssetType === 'charger') updateData.charger = newAssetNumber.trim();
        if (newAssetType === 'headphone') updateData.headphone = newAssetNumber.trim();
        if (newAssetType === 'phoneNumber') updateData.phoneNumber = newAssetNumber.trim();

        await employeeService.update(employeeId, updateData);
        toast.success(`Asset added and assigned successfully to ${employee.firstName} ${employee.lastName}`);

        // Reset form
        setNewAssetType('laptop');
        setNewAssetNumber('');
        setNewAssetEmployeeId('');
        setShowAddModal(false);
        fetchEmployees();
      }
    } catch (error) {
      console.error('Error adding asset:', error);
      toast.error('Failed to add asset');
    } finally {
      setSubmitting(false);
    }
  };

  const parseCSV = (text: string): Array<{ assetType: string; assetNumber: string; employeeIdentifier: string }> => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return [];

    // Check if first line is header
    const hasHeader = lines[0].toLowerCase().includes('asset') || lines[0].toLowerCase().includes('employee');
    const dataLines = hasHeader ? lines.slice(1) : lines;

    return dataLines.map((line, index) => {
      const columns = line.split(',').map(col => col.trim().replace(/^"|"$/g, ''));
      
      // Expected format: Asset Type, Asset Number, Employee Code/Email (optional)
      if (columns.length < 2) {
        throw new Error(`Row ${index + (hasHeader ? 2 : 1)}: Invalid format. Expected: Asset Type, Asset Number, Employee Code/Email (optional)`);
      }

      return {
        assetType: columns[0].trim(),
        assetNumber: columns[1].trim(),
        employeeIdentifier: columns[2] ? columns[2].trim() : '', // Optional employee field
      };
    });
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setSubmitting(true);
    setBulkUploadProgress({ total: 0, success: 0, failed: 0, errors: [] });

    try {
      const text = await bulkUploadFile.text();
      const parsedData = parseCSV(text);

      if (parsedData.length === 0) {
        toast.error('No data found in CSV file');
        setSubmitting(false);
        return;
      }

      setBulkUploadProgress({ total: parsedData.length, success: 0, failed: 0, errors: [] });

      let successCount = 0;
      let failedCount = 0;
      const errors: string[] = [];

      // Process each row
      for (let i = 0; i < parsedData.length; i++) {
        const row = parsedData[i];
        try {
          // Validate asset type
          const assetType = row.assetType.toLowerCase();
          if (!['laptop', 'charger', 'headphone', 'phonenumber', 'phone'].includes(assetType)) {
            throw new Error(`Invalid asset type: ${row.assetType}. Must be: Laptop, Charger, Headphone, or PhoneNumber`);
          }

          const normalizedAssetType = assetType === 'phone' || assetType === 'phonenumber' ? 'phoneNumber' : assetType as AssetType;

          // If no employee identifier provided, store as unassigned asset
          if (!row.employeeIdentifier || row.employeeIdentifier.trim() === '') {
            // Check if asset already exists (assigned or unassigned)
            const existingAssets = getAllAssets();
            const assetExists = existingAssets.some(
              (a) => a.type === normalizedAssetType && a.assetNumber === row.assetNumber
            );

            if (assetExists) {
              throw new Error(`Asset ${row.assetNumber} already exists`);
            }

            // Store as unassigned asset in localStorage
            const unassignedAssets = getUnassignedAssets();
            unassignedAssets.push({
              type: normalizedAssetType,
              assetNumber: row.assetNumber,
              employeeId: null,
              employeeName: 'Unassigned',
              employeeCode: '-',
              isUnassigned: true,
            });
            saveUnassignedAssets(unassignedAssets);
            successCount++;
          } else {
            // Find employee by code or email
            const employee = employees.find(
              (e) => e.employeeCode.toLowerCase() === row.employeeIdentifier.toLowerCase() ||
                     e.email.toLowerCase() === row.employeeIdentifier.toLowerCase()
            );

            if (!employee) {
              throw new Error(`Employee not found: ${row.employeeIdentifier}`);
            }

            // Check if employee already has this asset type
            if (normalizedAssetType === 'laptop' && employee.laptop) {
              throw new Error(`Employee ${employee.employeeCode} already has a laptop assigned`);
            }
            if (normalizedAssetType === 'charger' && employee.charger) {
              throw new Error(`Employee ${employee.employeeCode} already has a charger assigned`);
            }
            if (normalizedAssetType === 'headphone' && employee.headphone) {
              throw new Error(`Employee ${employee.employeeCode} already has a headphone assigned`);
            }
            if (normalizedAssetType === 'phoneNumber' && employee.phoneNumber) {
              throw new Error(`Employee ${employee.employeeCode} already has a phone number assigned`);
            }

            // Assign the asset
            const updateData: any = {};
            if (normalizedAssetType === 'laptop') updateData.laptop = row.assetNumber;
            if (normalizedAssetType === 'charger') updateData.charger = row.assetNumber;
            if (normalizedAssetType === 'headphone') updateData.headphone = row.assetNumber;
            if (normalizedAssetType === 'phoneNumber') updateData.phoneNumber = row.assetNumber;

            await employeeService.update(employee.id, updateData);
            successCount++;
          }

          setBulkUploadProgress({
            total: parsedData.length,
            success: successCount,
            failed: failedCount,
            errors: [...errors],
          });
        } catch (error: any) {
          failedCount++;
          const errorMsg = `Row ${i + 2}: ${error.message || 'Unknown error'}`;
          errors.push(errorMsg);

          setBulkUploadProgress({
            total: parsedData.length,
            success: successCount,
            failed: failedCount,
            errors: [...errors],
          });
        }
      }

      // Show summary
      if (successCount > 0) {
        toast.success(`Successfully assigned ${successCount} asset(s)`);
      }
      if (failedCount > 0) {
        toast.error(`${failedCount} asset(s) failed to assign. Check errors below.`);
      }

      // Refresh employee list and trigger re-render for unassigned assets
      if (successCount > 0) {
        await fetchEmployees();
        // Force re-render to show unassigned assets
        window.dispatchEvent(new Event('storage'));
      }
    } catch (error: any) {
      console.error('Error processing bulk upload:', error);
      toast.error(`Failed to process file: ${error.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadSampleCSV = () => {
    // Create sample CSV content
    const sampleData = [
      ['Asset Type', 'Asset Number', 'Employee Code/Email'],
      ['Laptop', 'LAP-001', 'EMP001'],
      ['Charger', 'CHG-001', 'EMP002'],
      ['Headphone', 'HP-001', 'john.doe@example.com'],
      ['PhoneNumber', 'PHN-001', 'EMP003'],
      ['Laptop', 'LAP-002', 'jane.smith@example.com'],
      ['Charger', 'CHG-002', ''],
      ['Headphone', 'HP-002', ''],
    ];

    const csvContent = sampleData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'asset_upload_sample.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Sample CSV file downloaded');
  };

  // Group assets by type for summary
  const assetsByType = {
    laptop: assets.filter((a) => a.type === 'laptop'),
    charger: assets.filter((a) => a.type === 'charger'),
    headphone: assets.filter((a) => a.type === 'headphone'),
    phoneNumber: assets.filter((a) => a.type === 'phoneNumber'),
  };

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Asset Management</h1>
          <p className="text-gray-500 mt-1">Manage and track company assets assigned to employees</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowBulkUploadModal(true)}>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Bulk Upload
          </Button>
          <Button onClick={() => setShowAddModal(true)}>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Asset
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Laptops</p>
                <p className="text-2xl font-bold text-gray-900">{assetsByType.laptop.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Chargers</p>
                <p className="text-2xl font-bold text-gray-900">{assetsByType.charger.length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Headphones</p>
                <p className="text-2xl font-bold text-gray-900">{assetsByType.headphone.length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Phones</p>
                <p className="text-2xl font-bold text-gray-900">{assetsByType.phoneNumber.length}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Input
              placeholder="Search by asset number, employee name, or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <Select
              options={[
                { value: 'all', label: 'All Asset Types' },
                { value: 'laptop', label: 'Laptop' },
                { value: 'charger', label: 'Charger' },
                { value: 'headphone', label: 'Headphone' },
                { value: 'phoneNumber', label: 'Phone Number' },
              ]}
              value={filterAssetType}
              onChange={(e) => setFilterAssetType(e.target.value as AssetType | 'all')}
            />
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setFilterAssetType('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Assets List */}
      <Card className="overflow-hidden">
        <CardHeader title="All Assets" />
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner w-8 h-8 border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <p className="text-gray-500">No assets found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Asset Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Asset Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee Code
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAssets.map((asset, index) => (
                  <tr key={`${asset.type}-${asset.assetNumber}-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {assetTypeLabels[asset.type]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{asset.assetNumber}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {asset.employeeId || asset.isUnassigned ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {asset.isUnassigned ? (
                              <span className="text-orange-600 font-semibold">Unassigned</span>
                            ) : (
                              asset.employeeName
                            )}
                          </div>
                          {!asset.isUnassigned && (
                            <div className="text-sm text-gray-500">{asset.employeeCode}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Unassigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {asset.isUnassigned ? (
                          <span className="text-orange-600">-</span>
                        ) : (
                          asset.employeeCode || '-'
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openAssignModal(asset)}
                      >
                        {asset.employeeId || asset.isUnassigned ? (asset.isUnassigned ? 'Assign' : 'Reassign') : 'Assign'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Assign/Reassign Modal */}
      {showAssignModal && selectedAsset && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowAssignModal(false)} />

            <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedAsset.employeeId ? 'Reassign Asset' : 'Assign Asset'}
                </h3>
              </div>

              <div className="px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset Type
                    </label>
                    <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-900">
                      {assetTypeLabels[selectedAsset.type]}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset Number
                    </label>
                    <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-900">
                      {selectedAsset.assetNumber}
                    </div>
                  </div>

                  {selectedAsset.employeeId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Currently Assigned To
                      </label>
                      <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-900">
                        {selectedAsset.employeeName} ({selectedAsset.employeeCode})
                      </div>
                    </div>
                  )}

                  <div>
                    <Select
                      label="Assign To Employee"
                      value={selectedEmployeeId}
                      onChange={(e) => setSelectedEmployeeId(e.target.value)}
                      options={[
                        { value: '', label: 'Unassign (Remove from current employee)' },
                        ...employees
                          .filter((emp) => {
                            // Don't show current employee if reassigning
                            if (selectedAsset.employeeId && emp.id === selectedAsset.employeeId) {
                              return false;
                            }
                            // Check if employee already has this asset type
                            if (selectedAsset.type === 'laptop' && emp.laptop) return false;
                            if (selectedAsset.type === 'charger' && emp.charger) return false;
                            if (selectedAsset.type === 'headphone' && emp.headphone) return false;
                            if (selectedAsset.type === 'phoneNumber' && emp.phoneNumber) return false;
                            return true;
                          })
                          .map((emp) => ({
                            value: String(emp.id),
                            label: `${emp.firstName} ${emp.lastName} (${emp.employeeCode})`,
                          })),
                      ]}
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowAssignModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAssignAsset} isLoading={submitting}>
                  {selectedAsset.employeeId ? 'Reassign' : 'Assign'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Asset Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowAddModal(false)} />

            <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Add New Asset</h3>
              </div>

              <div className="px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <Select
                      label="Asset Type"
                      value={newAssetType}
                      onChange={(e) => setNewAssetType(e.target.value as AssetType)}
                      options={[
                        { value: 'laptop', label: 'Laptop' },
                        { value: 'charger', label: 'Charger' },
                        { value: 'headphone', label: 'Headphone' },
                        { value: 'phoneNumber', label: 'Phone Number' },
                      ]}
                    />
                  </div>

                  <div>
                    <Input
                      label="Asset Number"
                      placeholder="Enter asset number or serial number"
                      value={newAssetNumber}
                      onChange={(e) => setNewAssetNumber(e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <Select
                      label="Assign To Employee"
                      value={newAssetEmployeeId}
                      onChange={(e) => setNewAssetEmployeeId(e.target.value)}
                      options={[
                        { value: '', label: 'Select an employee...' },
                        ...employees
                          .filter((emp) => {
                            // Filter out employees who already have this asset type
                            if (newAssetType === 'laptop' && emp.laptop) return false;
                            if (newAssetType === 'charger' && emp.charger) return false;
                            if (newAssetType === 'headphone' && emp.headphone) return false;
                            if (newAssetType === 'phoneNumber' && emp.phoneNumber) return false;
                            return true;
                          })
                          .map((emp) => ({
                            value: String(emp.id),
                            label: `${emp.firstName} ${emp.lastName} (${emp.employeeCode})`,
                          })),
                      ]}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <Button variant="outline" onClick={() => {
                  setShowAddModal(false);
                  setNewAssetType('laptop');
                  setNewAssetNumber('');
                  setNewAssetEmployeeId('');
                }}>
                  Cancel
                </Button>
                <Button onClick={handleAddAsset} isLoading={submitting}>
                  Add Asset
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => {
              if (!submitting) {
                setShowBulkUploadModal(false);
                setBulkUploadFile(null);
                setBulkUploadProgress(null);
              }
            }} />

            <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Bulk Upload Assets</h3>
                <p className="text-sm text-gray-500 mt-1">Upload a CSV file to assign multiple assets at once</p>
              </div>

              <div className="px-6 py-4">
                <div className="space-y-4">
                  {/* CSV Format Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-semibold text-blue-900">CSV Format:</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={downloadSampleCSV}
                        className="text-xs"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download Sample
                      </Button>
                    </div>
                    <p className="text-xs text-blue-800 mb-2">Your CSV file should have the following columns:</p>
                    <div className="bg-white rounded p-2 text-xs font-mono text-blue-900">
                      Asset Type, Asset Number, Employee Code/Email
                    </div>
                    <p className="text-xs text-blue-800 mt-2">
                      <strong>Asset Type:</strong> Laptop, Charger, Headphone, or PhoneNumber<br />
                      <strong>Asset Number:</strong> Serial number or identifier<br />
                      <strong>Employee Code/Email:</strong> Employee code or email address (optional - leave empty to assign later)
                    </p>
                    <div className="mt-3 bg-white rounded p-2 text-xs font-mono text-blue-900">
                      Example:<br />
                      Laptop, LAP-001, EMP001<br />
                      Charger, CHG-001, john.doe@example.com<br />
                      Headphone, HP-001, EMP002<br />
                      PhoneNumber, PHN-001,<br />
                      (Leave employee field empty to assign later)
                    </div>
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select CSV File
                    </label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-500 transition-colors">
                      <div className="space-y-1 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                            <span>Upload a file</span>
                            <input
                              type="file"
                              accept=".csv"
                              className="sr-only"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setBulkUploadFile(file);
                                  setBulkUploadProgress(null);
                                }
                              }}
                              disabled={submitting}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">CSV files only</p>
                        {bulkUploadFile && (
                          <p className="text-sm text-gray-900 mt-2">
                            Selected: <span className="font-medium">{bulkUploadFile.name}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Progress/Results */}
                  {bulkUploadProgress && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Progress</span>
                        <span className="text-sm text-gray-600">
                          {bulkUploadProgress.success + bulkUploadProgress.failed} / {bulkUploadProgress.total}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${((bulkUploadProgress.success + bulkUploadProgress.failed) / bulkUploadProgress.total) * 100}%`,
                          }}
                        />
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span className="text-green-600">✓ Success: {bulkUploadProgress.success}</span>
                        <span className="text-red-600">✗ Failed: {bulkUploadProgress.failed}</span>
                      </div>

                      {bulkUploadProgress.errors.length > 0 && (
                        <div className="mt-4 max-h-40 overflow-y-auto">
                          <p className="text-xs font-medium text-gray-700 mb-1">Errors:</p>
                          <div className="bg-white rounded p-2 text-xs text-red-600 space-y-1">
                            {bulkUploadProgress.errors.map((error, idx) => (
                              <div key={idx}>{error}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!submitting) {
                      setShowBulkUploadModal(false);
                      setBulkUploadFile(null);
                      setBulkUploadProgress(null);
                    }
                  }}
                  disabled={submitting}
                >
                  {submitting ? 'Processing...' : 'Cancel'}
                </Button>
                <Button
                  onClick={handleBulkUpload}
                  isLoading={submitting}
                  disabled={!bulkUploadFile || submitting}
                >
                  Upload & Process
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
